import { useQuery } from '@tanstack/react-query';
import { ArrowRight, CalendarDays, ChevronDown, ChevronRight, MapPin, Search } from 'lucide-react';
import { lazy, Suspense, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CardGridSkeleton, gridCols, itemPath, ListingCard } from '@/components/common/Cards';
import { SectionHeading } from '@/components/common/Layout';
import { PhotoGrid } from '@/components/common/Lightbox';
import { HeroScene } from '@/components/common/Scene';
import { SmartImage } from '@/components/common/SmartImage';
import { tone } from '@/components/common/tiles';
import { api } from '@/lib/api';
import { Button, EmptyState, Icon } from '@/components/ui';
import { useI18n } from '@/lib/i18n';
import type { Block, ImageRef, Localized, PublicItem } from '@/lib/types';
import { cn, formatDate, isExternal, isSafeUrl } from '@/lib/utils';
import { isDark, Section } from './Section';

const Markdown = lazy(() => import('@/components/common/Markdown'));
const LazyMap = lazy(() => import('@/components/common/MapView'));

function CtaButton({ b, size = 'lg' }: { b: { label: Localized; url: string; variant?: string }; size?: 'md' | 'lg' }) {
  const { L } = useI18n();
  if (!isSafeUrl(b.url)) return null;
  const variant = (b.variant === 'outline' ? 'outline' : b.variant ?? 'primary') as any;
  const content = <>{L(b.label)}<ArrowRight /></>;
  if (isExternal(b.url)) return <a href={b.url} target="_blank" rel="noopener noreferrer"><Button variant={variant} size={size}>{content}</Button></a>;
  return <Link to={b.url}><Button variant={variant} size={size} tabIndex={-1}>{content}</Button></Link>;
}

function ViewAll({ url, dark }: { url?: string; dark?: boolean }) {
  const { t } = useI18n();
  if (!url || !isSafeUrl(url)) return null;
  return <Link to={url} className={cn('inline-flex items-center gap-1 text-sm font-semibold', dark ? 'text-gold-300 hover:text-gold-200' : 'text-brand-800 hover:text-brand-950')}>{t('viewAll')} <ArrowRight className="size-4" /></Link>;
}

function Heading({ b, title, description, action }: { b: Block; title?: Localized; description?: Localized; action?: React.ReactNode }) {
  const { L } = useI18n();
  if (!L(title) && !L(description)) return null;
  return <SectionHeading title={L(title)} description={L(description)} action={action} align={b.settings?.align} tone={isDark(b) ? 'light' : 'default'} />;
}

function HeroSearch({ placeholder }: { placeholder: string }) {
  const navigate = useNavigate();
  const { t, L, lang } = useI18n();
  const [q, setQ] = useState('');
  const [village, setVillage] = useState('');
  const villages = useQuery({ queryKey: ['options', 'village'], queryFn: () => api.get<{ _id: string; label: Localized }[]>('/api/public/options?kind=village').then((r) => r.data), staleTime: 300_000 });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!q.trim()) return navigate(village ? `/businesses?village=${village}` : '/search');
    navigate(`/search?q=${encodeURIComponent(q.trim())}${village ? `&village=${village}` : ''}`);
  };
  return (
    <form onSubmit={submit} role="search" className="mt-7 flex w-full max-w-2xl items-center rounded-full bg-white p-1.5 pl-5 shadow-lift ring-1 ring-black/5">
      <label htmlFor="hero-search" className="sr-only">{t('search')}</label>
      <input id="hero-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} className="h-11 min-w-0 flex-1 bg-transparent text-[15px] text-ink placeholder:text-ink-muted focus:outline-none" />
      <span className="mx-2 hidden h-7 w-px bg-line sm:block" aria-hidden />
      <label htmlFor="hero-village" className="sr-only">{t('allVillages')}</label>
      <MapPin className="hidden size-4 shrink-0 text-brand-600 sm:block" aria-hidden />
      <select id="hero-village" value={village} onChange={(e) => setVillage(e.target.value)} className="hidden h-11 max-w-[11rem] cursor-pointer bg-transparent pl-1.5 pr-2 text-sm text-ink-soft focus:outline-none sm:block">
        <option value="">{lang === 'te' ? 'అన్ని ప్రాంతాలు' : 'All areas'}</option>
        {(villages.data ?? []).map((v) => <option key={v._id} value={v._id}>{L(v.label)}</option>)}
      </select>
      <button type="submit" aria-label={t('search')} className="ml-1 flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white shadow-md transition hover:bg-brand-700"><Search className="size-5" /></button>
    </form>
  );
}

const STAT_ICONS: Record<string, string> = { places: 'MapPin', businesses: 'Store', verified_businesses: 'Shield', temples: 'Landmark', villages: 'Trees', events: 'CalendarDays', articles: 'Newspaper', photos: 'Images', categories: 'Tag', jobs: 'Briefcase' };
const STAT_LABELS: Record<string, Localized> = {
  places: { en: 'Places', te: 'ప్రదేశాలు' }, businesses: { en: 'Businesses', te: 'వ్యాపారాలు' }, verified_businesses: { en: 'Verified businesses', te: 'ధృవీకరించినవి' },
  temples: { en: 'Temples', te: 'దేవాలయాలు' }, villages: { en: 'Villages', te: 'గ్రామాలు' }, events: { en: 'Events', te: 'కార్యక్రమాలు' },
  articles: { en: 'Stories', te: 'కథలు' }, photos: { en: 'Photos', te: 'ఫోటోలు' }, categories: { en: 'Categories', te: 'వర్గాలు' },
  jobs: { en: 'Open jobs', te: 'ఉద్యోగాలు' },
};

function HeroBlock({ b }: { b: Block }) {
  const { L, t, lang } = useI18n();
  const images: ImageRef[] = b.images ?? [];
  // "Show whole image" on phones: the image keeps its own shape above the text instead of being cropped to fill a tall box.
  const whole = b.mobileImageFit === 'contain' && !!images[0];
  const height = whole
    ? ({ md: 'md:min-h-[440px]', lg: 'md:min-h-[600px]', full: 'md:min-h-[calc(100svh-4rem)]' }[b.height as string] ?? 'md:min-h-[560px]')
    : ({ md: 'min-h-[440px]', lg: 'min-h-[540px] md:min-h-[600px]', full: 'min-h-[calc(100svh-4rem)]' }[b.height as string] ?? 'min-h-[560px]');
  const tiles: any[] = b.tiles ?? [];
  const stats: string[] = b.stats ?? [];
  const counts = b.data?.counts ?? {};
  const center = b.settings?.align === 'center';
  const [first, ...rest] = (b.heading?.en || '').split(' ');
  const quote = L(b.sideQuote);
  return (
    <Section block={b} container={false} className="!py-0">
      <div className={cn('relative isolate flex overflow-hidden bg-brand-900', whole ? 'flex-col md:flex-row md:items-center' : 'items-center', height, tiles.length && 'pb-20')}>
        {whole && <SmartImage image={images[0]} priority rounded={false} sizes="100vw" className="h-auto w-full md:hidden" />}
        {images[0] ? <SmartImage image={images[0]} priority={!whole} rounded={false} sizes="100vw" className={cn('absolute inset-0 -z-20 h-full w-full', whole && 'hidden md:block')} /> : <HeroScene className="absolute inset-0 -z-20 h-full w-full" />}
        <div className={cn('absolute inset-0 -z-10 bg-gradient-to-r from-brand-950/75 via-brand-950/35 to-transparent', whole && 'hidden md:block')} style={{ opacity: Math.min(1, (b.overlay ?? 35) / 50 + 0.3) }} />
        <div className="container grid items-center gap-8 py-14 lg:grid-cols-[1.25fr_1fr]">
          <div className={cn('max-w-2xl text-white [text-shadow:0_2px_16px_rgba(10,20,60,.35)]', center && 'mx-auto text-center [&_form]:mx-auto lg:col-span-2')}>
            {L(b.subheading) && <p className="font-display text-2xl font-semibold text-white/90 md:text-3xl">{L(b.subheading)}</p>}
            <h1 className="mt-1 text-white">
              {b.heading?.en && <span className="block font-display text-5xl font-extrabold leading-[1.05] tracking-tight text-white md:text-6xl xl:text-7xl">{first} <span className="text-forest-300">{rest.join(' ')}</span></span>}
              {b.heading?.te && <span className={cn('block font-telugu font-bold', b.heading?.en ? 'mt-2 text-3xl text-white/95 md:text-4xl' : 'text-5xl md:text-7xl')}>{b.heading.te}</span>}
            </h1>
            {L(b.tagline) && <p className="mt-3 font-script text-3xl text-white md:text-4xl">{L(b.tagline)}</p>}
            {L(b.body) && <p className="mt-3 max-w-xl text-base text-white/85 md:text-lg">{L(b.body)}</p>}
            {b.showSearch && <HeroSearch placeholder={L(b.searchPlaceholder) || t('searchPlaceholder')} />}
            {b.buttons?.length > 0 && <div className={cn('mt-6 flex flex-wrap gap-3', center && 'justify-center')}>{b.buttons.map((btn: any, i: number) => <CtaButton key={i} b={{ ...btn, variant: btn.variant === 'outline' ? 'outline-light' : btn.variant }} />)}</div>}
          </div>
          {quote && !center && (
            <p className="hidden justify-self-end whitespace-pre-line text-right font-script text-5xl leading-tight text-white [text-shadow:0_2px_18px_rgba(10,20,60,.45)] lg:block">{quote}</p>
          )}
        </div>
      </div>
      {(tiles.length > 0 || stats.length > 0) && (
        <div className="container relative z-10 -mt-20">
          <div className="rounded-3xl bg-white p-4 shadow-lift ring-1 ring-black/5 md:p-6">
            {tiles.length > 0 && (
              <nav aria-label={lang === 'te' ? 'త్వరిత లింకులు' : 'Quick links'} className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-flow-col lg:auto-cols-fr lg:grid-cols-none">
                {tiles.filter((x) => isSafeUrl(x.url)).map((x, i) => (
                  <Link key={i} to={x.url} className="group flex flex-col items-center gap-2 rounded-2xl px-2 py-3 text-center transition hover:bg-surface-muted">
                    <span className={cn('flex size-14 items-center justify-center rounded-2xl ring-1 transition group-hover:-translate-y-0.5 group-hover:shadow-md', tone(x.color, i))}><Icon name={x.icon} className="size-7" /></span>
                    <span className="text-[13px] font-semibold text-ink">{L(x.label)}</span>
                  </Link>
                ))}
              </nav>
            )}
            {stats.length > 0 && (
              <dl className={cn('grid grid-cols-2 gap-4 sm:grid-cols-4', tiles.length && 'mt-4 border-t border-line pt-4')}>
                {stats.map((k) => (
                  <div key={k} className="flex items-center gap-3 px-2">
                    <span className="flex size-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600"><Icon name={STAT_ICONS[k]} className="size-5" /></span>
                    <div>
                      <dd className="font-display text-xl font-bold text-ink">{(counts[k] ?? 0).toLocaleString(lang === 'te' ? 'te-IN' : 'en-IN')}</dd>
                      <dt className="text-xs text-ink-muted">{L(STAT_LABELS[k])}</dt>
                    </div>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}

function CategoryGridBlock({ b }: { b: Block }) {
  const { L } = useI18n();
  const cats: any[] = b.data ?? [];
  const base = { business: '/businesses', place: '/places', temple: '/temples', event: '/events', article: '/articles' }[b.listingType as string] ?? '/businesses';
  return (
    <Section block={b}>
      <Heading b={b} title={b.title} description={b.description} />
      {cats.length === 0 ? <EmptyState title="No categories yet" /> : (
        <div className={cn('grid grid-cols-2 gap-3', b.settings?.columns === 4 ? 'md:grid-cols-4 lg:grid-cols-6' : 'md:grid-cols-3 lg:grid-cols-4')}>
          {cats.map((c, i) =>
            b.style === 'images' ? (
              <Link key={c._id} to={`${base}?category=${encodeURIComponent(c.slug)}`} className="group relative aspect-[4/3] overflow-hidden rounded-2xl">
                <SmartImage image={c.image} rounded={false} className="h-full w-full transition group-hover:scale-105" sizes="25vw" />
                <span className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <span className="absolute bottom-3 left-3 right-3 font-semibold text-white">{L(c.name)}</span>
              </Link>
            ) : (
              <Link key={c._id} to={`${base}?category=${encodeURIComponent(c.slug)}`} className={cn('group flex flex-col items-start gap-3 rounded-2xl border p-4 transition hover:-translate-y-0.5 hover:shadow-lift', isDark(b) ? 'border-white/15 bg-white/5 hover:bg-white/10' : 'border-line bg-white hover:border-brand-200')}>
                <span className={cn('flex size-12 items-center justify-center rounded-2xl ring-1 transition group-hover:scale-105', isDark(b) ? 'bg-white/10 text-gold-300 ring-white/10' : tone(undefined, i))}><Icon name={c.icon} className="size-6" /></span>
                <span className="text-sm font-semibold leading-snug">{L(c.name)}</span>
              </Link>
            ),
          )}
        </div>
      )}
    </Section>
  );
}

function ListingsBlock({ b }: { b: Block }) {
  const { lang } = useI18n();
  const items: PublicItem[] = b.data ?? [];
  const isCarousel = b.layout === 'carousel';
  return (
    <Section block={b}>
      <Heading b={b} title={b.title} description={b.description} action={<ViewAll url={b.viewAllUrl} dark={isDark(b)} />} />
      {items.length === 0 && b.source === 'job' ? (
        <EmptyState title={lang === 'te' ? 'ప్రస్తుతం ఉద్యోగ ఖాళీలు లేవు' : 'No open jobs right now'} description={lang === 'te' ? 'మీరు ఉద్యోగుల కోసం చూస్తున్నారా? మీ ఖాళీని ఉచితంగా ప్రకటించండి.' : 'Hiring? Post your opening for free. A moderator reviews it before it goes live.'} action={<Link to="/submit/job"><Button size="sm" tabIndex={-1}>{lang === 'te' ? 'ఉద్యోగ ప్రకటన ఇవ్వండి' : 'Post a job'}</Button></Link>} />
      ) : items.length === 0 ? (
        <EmptyState title="Nothing to show yet" description="Content will appear here once it is published." />
      ) : b.layout === 'list' ? (
        <div className="grid gap-3 md:grid-cols-2">{items.map((it) => <ListingCard key={it._id} item={it} layout="list" />)}</div>
      ) : (
        <div className={isCarousel ? '-mx-4 flex snap-x gap-4 overflow-x-auto px-4 pb-2 scrollbar-none [&>*]:w-72 [&>*]:shrink-0 [&>*]:snap-start' : cn('grid gap-5', gridCols(b.settings?.columns))}>
          {items.map((it) => <ListingCard key={it._id} item={it} />)}
        </div>
      )}
    </Section>
  );
}

function DirectoryBlock({ b }: { b: Block }) {
  const { L, t } = useI18n();
  const items: PublicItem[] = b.data?.items ?? [];
  const cats: any[] = b.data?.categories ?? [];
  return (
    <Section block={b}>
      <Heading b={b} title={b.title} description={b.description} action={<ViewAll url="/businesses" />} />
      {cats.length > 0 && (
        <div className="-mx-4 mb-5 flex gap-2 overflow-x-auto px-4 pb-1 scrollbar-none">
          {cats.map((c) => (
            <Link key={c._id} to={`/businesses?category=${encodeURIComponent(c.slug)}`} className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-line bg-white px-3 py-1.5 text-sm font-medium text-ink-soft hover:border-brand-300 hover:text-brand-900">
              <Icon name={c.icon} className="size-4 text-brand-700" />{L(c.name)}
            </Link>
          ))}
        </div>
      )}
      {items.length === 0 ? <EmptyState title={t('noResults')} /> : <div className={cn('grid gap-5', gridCols(b.settings?.columns))}>{items.map((it) => <ListingCard key={it._id} item={it} />)}</div>}
    </Section>
  );
}

function TimelineBlock({ b }: { b: Block }) {
  const { L } = useI18n();
  const items = b.source === 'manual' ? (b.items ?? []).map((i: any, idx: number) => ({ key: idx, year: i.year, title: L(i.title), body: L(i.body) })) : (b.data ?? []).map((r: any) => ({ key: r._id, year: r.period || r.year, title: L(r.title), body: L(r.summary), href: itemPath(r) }));
  return (
    <Section block={b}>
      <Heading b={b} title={b.title} description={b.description} />
      {items.length === 0 ? <EmptyState title="No timeline entries yet" /> : (
        <ol className="relative ml-3 border-l-2 border-gold-300">
          {items.map((it: any) => (
            <li key={it.key} className="mb-8 ml-6 last:mb-0">
              <span className="absolute -left-[9px] mt-1.5 size-4 rounded-full border-2 border-white bg-gold-400 shadow" />
              <p className="text-sm font-bold text-gold-700">{it.year}</p>
              <h3 className="mt-0.5 text-lg font-semibold">{it.href ? <Link to={it.href} className="hover:text-brand-800">{it.title}</Link> : it.title}</h3>
              {it.body && <p className={cn('mt-1 max-w-2xl text-sm', isDark(b) ? 'text-white/75' : 'text-ink-muted')}>{it.body}</p>}
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
}

function TextImageBlock({ b }: { b: Block }) {
  const { L } = useI18n();
  const aspect = { square: 'aspect-square', landscape: 'aspect-[4/3]', portrait: 'aspect-[3/4]' }[b.aspect as string] ?? 'aspect-[4/3]';
  return (
    <Section block={b}>
      <div className={cn('grid items-center gap-8 md:gap-12', b.image?.url && 'md:grid-cols-2')}>
        <div className={cn(b.imagePosition === 'left' && 'md:order-2')}>
          {L(b.heading) && <h2 className="font-display text-2xl font-semibold md:text-3xl">{L(b.heading)}</h2>}
          <Suspense fallback={null}><Markdown className={cn('prose-ylm mt-3', isDark(b) && '[&_*]:!text-white/85')}>{L(b.body)}</Markdown></Suspense>
          {b.buttons?.length > 0 && <div className="mt-5 flex flex-wrap gap-3">{b.buttons.map((btn: any, i: number) => <CtaButton key={i} b={btn} size="md" />)}</div>}
        </div>
        {b.image?.url && <SmartImage image={b.image} className={cn('w-full rounded-3xl shadow-lift', aspect)} sizes="(max-width: 768px) 100vw, 50vw" />}
      </div>
    </Section>
  );
}

function RichTextBlock({ b }: { b: Block }) {
  const { L } = useI18n();
  return (
    <Section block={b}>
      {L(b.heading) && <h2 className="mb-4 font-display text-2xl font-semibold md:text-3xl">{L(b.heading)}</h2>}
      <Suspense fallback={<div className="skeleton h-40 rounded-xl" />}><Markdown>{L(b.body)}</Markdown></Suspense>
    </Section>
  );
}

function EventsBlock({ b }: { b: Block }) {
  const { L, lang } = useI18n();
  const items: PublicItem[] = b.data ?? [];
  return (
    <Section block={b}>
      <Heading b={b} title={b.title} description={b.description} action={<ViewAll url={b.viewAllUrl} dark={isDark(b)} />} />
      {items.length === 0 ? <EmptyState icon={<CalendarDays className="size-6" />} title="No upcoming events" description="Check back soon, or submit an event." action={<Link to="/submit/event"><Button variant="outline" size="sm">Submit an event</Button></Link>} /> : (
        <div className={cn('grid gap-4', gridCols(b.settings?.columns))}>
          {items.map((e) => {
            const d = new Date(e.startsAt!);
            return (
              <Link key={e._id} to={itemPath(e)} className="group flex gap-4 rounded-2xl border border-line bg-white p-4 text-ink shadow-card transition hover:-translate-y-0.5 hover:shadow-lift">
                <div className="flex w-16 shrink-0 flex-col items-center justify-center rounded-xl bg-gold-50 py-2 text-gold-800 ring-1 ring-gold-200">
                  <span className="text-xs font-semibold uppercase">{formatDate(d, lang, { month: 'short' })}</span>
                  <span className="font-display text-2xl font-bold leading-none">{d.getDate()}</span>
                </div>
                <div className="min-w-0">
                  <h3 className="line-clamp-2 font-semibold group-hover:text-brand-800">{L(e.title)}</h3>
                  <p className="mt-1 text-xs text-ink-muted">{formatDate(d, lang, { weekday: 'long', hour: e.allDay ? undefined : 'numeric', minute: e.allDay ? undefined : '2-digit' })}</p>
                  {(L(e.venue?.name) || e.village) && <p className="mt-1 flex items-center gap-1 text-xs text-ink-muted"><MapPin className="size-3" />{L(e.venue?.name) || L(e.village?.name)}</p>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </Section>
  );
}

const METRIC_LABELS: Record<string, Localized> = {
  businesses: { en: 'Local businesses', te: 'స్థానిక వ్యాపారాలు' }, verified_businesses: { en: 'Verified businesses', te: 'ధృవీకరించిన వ్యాపారాలు' },
  places: { en: 'Places to explore', te: 'ప్రదేశాలు' }, temples: { en: 'Temples', te: 'దేవాలయాలు' }, villages: { en: 'Villages', te: 'గ్రామాలు' },
  events: { en: 'Events', te: 'కార్యక్రమాలు' }, articles: { en: 'Stories', te: 'కథలు' }, photos: { en: 'Community photos', te: 'ఫోటోలు' },
};

function StatsBlock({ b }: { b: Block }) {
  const { L, lang } = useI18n();
  const counts = b.data ?? {};
  return (
    <Section block={b}>
      <Heading b={b} title={b.title} description={b.description} />
      <dl className={cn('grid grid-cols-2 gap-4', (b.metrics?.length ?? 0) >= 4 ? 'md:grid-cols-4' : 'md:grid-cols-3')}>
        {(b.metrics ?? []).map((m: any) => (
          <div key={m.key} className={cn('rounded-2xl p-5', isDark(b) ? 'bg-white/5 ring-1 ring-white/10' : 'bg-white ring-1 ring-line')}>
            <dt className={cn('text-sm', isDark(b) ? 'text-white/70' : 'text-ink-muted')}>{L(m.label) || L(METRIC_LABELS[m.key])}</dt>
            <dd className="mt-1 font-display text-3xl font-bold">{(counts[m.key] ?? 0).toLocaleString(lang === 'te' ? 'te-IN' : 'en-IN')}</dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

function MapBlock({ b }: { b: Block }) {
  const pts: any[] = b.data ?? [];
  const { L } = useI18n();
  const height = { sm: 'h-64', md: 'h-96', lg: 'h-[32rem]' }[b.height as string] ?? 'h-96';
  return (
    <Section block={b}>
      <Heading b={b} title={b.title} description={b.description} />
      <Suspense fallback={<div className={cn('skeleton rounded-2xl', height)} />}>
        <LazyMap className={height} markers={pts.map((p) => ({ id: String(p._id), position: [p.coordinates[1], p.coordinates[0]], title: L(p.title), href: itemPath({ kind: p.kind, slug: p.slug }), kind: p.kind }))} />
      </Suspense>
    </Section>
  );
}

function CtaBlock({ b }: { b: Block }) {
  const { L } = useI18n();
  const tone = { teal: 'bg-brand-900 text-white', green: 'bg-forest-800 text-white', gold: 'bg-gold-300 text-ink', light: 'bg-brand-50 text-ink ring-1 ring-brand-100' }[b.tone as string] ?? 'bg-brand-900 text-white';
  const light = b.tone === 'gold' || b.tone === 'light';
  return (
    <Section block={b}>
      <div className={cn('relative overflow-hidden rounded-3xl px-6 py-10 md:px-12 md:py-14', tone, b.settings?.align === 'center' && 'text-center')}>
        {b.image?.url && <SmartImage image={b.image} rounded={false} className="absolute inset-0 h-full w-full opacity-20" sizes="100vw" />}
        <div className="pointer-events-none absolute -right-16 -top-16 size-64 rounded-full bg-gold-400/20 blur-3xl" aria-hidden />
        <div className={cn('relative', b.settings?.align === 'center' && 'mx-auto max-w-2xl')}>
          <h2 className="font-display text-2xl font-semibold md:text-4xl">{L(b.heading)}</h2>
          {L(b.body) && <p className={cn('mt-3 text-base md:text-lg', light ? 'text-ink-soft' : 'text-white/80')}>{L(b.body)}</p>}
          {b.buttons?.length > 0 && (
            <div className={cn('mt-6 flex flex-wrap gap-3', b.settings?.align === 'center' && 'justify-center')}>
              {b.buttons.map((btn: any, i: number) => <CtaButton key={i} b={{ ...btn, variant: btn.variant === 'outline' && !light ? 'outline-light' : btn.variant }} />)}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

function VideoBlock({ b }: { b: Block }) {
  const { L } = useI18n();
  const src = b.data?.embedUrl;
  return (
    <Section block={b}>
      <Heading b={b} title={b.title} />
      {src ? (
        <div className="aspect-video overflow-hidden rounded-2xl bg-black shadow-lift">
          <iframe src={src} title={L(b.title) || 'Video'} className="h-full w-full" loading="lazy" allow="accelerometer; encrypted-media; gyroscope; picture-in-picture" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" sandbox="allow-scripts allow-same-origin allow-presentation allow-popups" />
        </div>
      ) : <EmptyState title="Video unavailable" />}
      {L(b.caption) && <p className="mt-2 text-sm text-ink-muted">{L(b.caption)}</p>}
    </Section>
  );
}

function FaqBlock({ b }: { b: Block }) {
  const { L } = useI18n();
  return (
    <Section block={b}>
      <Heading b={b} title={b.title} />
      <div className="mx-auto max-w-3xl divide-y divide-line overflow-hidden rounded-2xl border border-line bg-white text-left text-ink">
        {(b.items ?? []).map((it: any, i: number) => (
          <details key={i} className="group p-5 [&_summary::-webkit-details-marker]:hidden">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold">
              {L(it.q)}
              <ChevronDown className="size-5 shrink-0 text-ink-muted transition group-open:rotate-180" />
            </summary>
            <Suspense fallback={null}><Markdown className="prose-ylm mt-3 text-sm">{L(it.a)}</Markdown></Suspense>
          </details>
        ))}
      </div>
    </Section>
  );
}

function LinkGridBlock({ b }: { b: Block }) {
  const { L } = useI18n();
  const links = (b.links ?? []).filter((l: any) => isSafeUrl(l.url));
  const linkProps = (url: string) => (isExternal(url) ? { as: 'a' as const, href: url, ...(url.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {}) } : { as: Link, to: url });
  if (b.style === 'list') {
    return (
      <Section block={b}>
        <Heading b={b} title={b.title} description={b.description} />
        <div className={cn('grid gap-3', (b.settings?.columns ?? 2) >= 2 && 'md:grid-cols-2')}>
          {links.map((l: any, i: number) => {
            const { as: C, ...p } = linkProps(l.url) as any;
            return (
              <C key={i} {...p} className="group flex items-center gap-4 rounded-2xl border border-line bg-white p-4 text-left shadow-card transition hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-lift">
                <span className={cn('flex size-12 shrink-0 items-center justify-center rounded-xl ring-1', tone(l.color, i))}><Icon name={l.icon} className="size-6" /></span>
                <span className="min-w-0 flex-1"><span className="block font-semibold text-ink">{L(l.label)}</span>{L(l.description) && <span className="mt-0.5 block text-sm text-ink-muted">{L(l.description)}</span>}</span>
                <ChevronRight className="size-5 text-ink-muted transition group-hover:translate-x-0.5 group-hover:text-brand-600" />
              </C>
            );
          })}
        </div>
      </Section>
    );
  }
  return (
    <Section block={b}>
      <Heading b={b} title={b.title} description={b.description} />
      <div className={cn('grid gap-4', gridCols(b.settings?.columns))}>
        {links.map((l: any, i: number) => {
          const inner = (
            <>
              {l.image?.url ? <SmartImage image={l.image} className="mb-3 aspect-[16/9] w-full" sizes="33vw" /> : <span className={cn('mb-3 flex size-12 items-center justify-center rounded-xl ring-1', tone(l.color, i))}><Icon name={l.icon} className="size-6" /></span>}
              <span className="font-semibold text-ink">{L(l.label)}</span>
              {L(l.description) && <span className="mt-1 block text-sm text-ink-muted">{L(l.description)}</span>}
            </>
          );
          const cls = 'block rounded-2xl border border-line bg-white p-5 text-left shadow-card transition hover:-translate-y-0.5 hover:shadow-lift';
          return isExternal(l.url) ? <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a> : <Link key={i} to={l.url} className={cls}>{inner}</Link>;
        })}
      </div>
    </Section>
  );
}

function GalleryBlock({ b }: { b: Block }) {
  const images: ImageRef[] = b.data?.images ?? b.images ?? [];
  const viewAll = b.viewAllUrl || (b.data?.gallerySlug ? `/gallery/${b.data.gallerySlug}` : undefined);
  return (
    <Section block={b}>
      <Heading b={b} title={b.title} description={b.description} action={<ViewAll url={viewAll} dark={isDark(b)} />} />
      {images.length === 0 ? <EmptyState title="No photos yet" description="Share your photos of Yelamanchili with the community." action={<Link to="/submit/photos"><Button variant="outline" size="sm">Submit photos</Button></Link>} /> : <PhotoGrid images={images} columns={b.settings?.columns ?? 4} aspect={b.aspect} layout={b.layout} />}
    </Section>
  );
}

const REGISTRY: Record<string, (p: { b: Block }) => React.ReactElement | null> = {
  hero: HeroBlock,
  category_grid: CategoryGridBlock,
  featured_listings: ListingsBlock,
  business_directory: DirectoryBlock,
  timeline: TimelineBlock,
  text_image: TextImageBlock,
  rich_text: RichTextBlock,
  article_list: ListingsBlock,
  events: EventsBlock,
  stats: StatsBlock,
  map: MapBlock,
  cta: CtaBlock,
  video: VideoBlock,
  faq: FaqBlock,
  link_grid: LinkGridBlock,
  image_gallery: GalleryBlock,
};

export function BlockRenderer({ blocks, loading }: { blocks?: Block[]; loading?: boolean }) {
  if (loading) {
    return (
      <div>
        <div className="skeleton h-[520px] w-full rounded-none" />
        <div className="container py-12"><CardGridSkeleton /></div>
      </div>
    );
  }
  return (
    <>
      {(blocks ?? []).filter((b) => !b.hidden).map((b) => {
        const C = REGISTRY[b.type];
        return C ? <C key={b.id} b={b} /> : null;
      })}
    </>
  );
}
